import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
  type CSSProperties,
} from "react";
import { createPortal } from "react-dom";
import type { TourStep } from "@/components/onboarding/tour-steps";

/**
 * Custom lightweight tour engine — replaces react-joyride.
 *
 * Provides an overlay with a box-shadow spotlight that tracks the target
 * element, a positioned tooltip, keyboard navigation, and scroll/resize
 * repositioning. No external dependencies.
 */

export type TourEndReason = "finished" | "skipped" | "closed" | "snoozed";

export interface TourEngineProps {
  steps: TourStep[];
  stepIndex: number;
  /** Render the tooltip content. The engine handles positioning + overlay. */
  renderTooltip: (params: {
    step: TourStep;
    index: number;
    size: number;
    isLastStep: boolean;
    onNext: () => void;
    onBack: () => void;
    onClose: () => void;
    onSnooze: () => void;
    onJump: (index: number) => void;
    tooltipRef: RefObject<HTMLDivElement | null>;
    tooltipStyle: CSSProperties;
  }) => ReactNode;
  onNext: () => void;
  onBack: () => void;
  onClose: () => void;
  onSnooze: () => void;
  onJump: (index: number) => void;
}

interface SpotRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const DEFAULT_SPOTLIGHT_PADDING = 8;
const DEFAULT_SPOTLIGHT_RADIUS = 12;

export const TourEngine = ({
  steps,
  stepIndex,
  renderTooltip,
  onNext,
  onBack,
  onClose,
  onSnooze,
  onJump,
}: TourEngineProps) => {
  const [spotRect, setSpotRect] = useState<SpotRect | null>(null);
  const [tooltipStyle, setTooltipStyle] = useState<CSSProperties>({});
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const targetElRef = useRef<Element | null>(null);

  const currentStep = steps[stepIndex];

  // Resolve the target element for the current step.
  const resolveTarget = useCallback((): Element | null => {
    if (!currentStep) return null;
    const target = currentStep.target;
    if (target === "body") return document.body;
    if (typeof target === "string") return document.querySelector(target);
    return null;
  }, [currentStep]);

  // Compute spotlight rect + tooltip position.
  const reposition = useCallback(() => {
    const el = resolveTarget();
    if (!el) return;
    targetElRef.current = el;

    const isBody = el === document.body;
    if (isBody) {
      setSpotRect(null);
      setTooltipStyle({
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 10001,
      });
      return;
    }

    const rect = el.getBoundingClientRect();
    const padding = currentStep?.data?.spotlightPadding ?? DEFAULT_SPOTLIGHT_PADDING;
    setSpotRect({
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    });

    // Position the tooltip relative to the spotlight.
    const placement = currentStep?.placement ?? "bottom";
    const gap = 16;
    const tooltipW = tooltipRef.current?.offsetWidth ?? 352;
    const tooltipH = tooltipRef.current?.offsetHeight ?? 220;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const spotTop = rect.top - padding;
    const spotLeft = rect.left - padding;
    const spotW = rect.width + padding * 2;
    const spotH = rect.height + padding * 2;
    const spotRight = spotLeft + spotW;
    const spotBottom = spotTop + spotH;

    let style: CSSProperties = { position: "fixed", zIndex: 10001 };

    const fitsBelow = spotBottom + gap + tooltipH < vh;
    const fitsAbove = spotTop - gap - tooltipH > 0;
    const fitsRight = spotRight + gap + tooltipW < vw;
    const fitsLeft = spotLeft - gap - tooltipW > 0;

    let resolvedPlacement = placement;
    // Auto-flip if there's no room.
    if (placement === "bottom" && !fitsBelow && fitsAbove) resolvedPlacement = "top";
    if (placement === "top" && !fitsAbove && fitsBelow) resolvedPlacement = "bottom";
    if (placement === "right" && !fitsRight && fitsLeft) resolvedPlacement = "left";
    if (placement === "left" && !fitsLeft && fitsRight) resolvedPlacement = "right";

    switch (resolvedPlacement) {
      case "top":
        style = {
          ...style,
          top: Math.max(8, spotTop - gap - tooltipH),
          left: Math.min(Math.max(8, spotLeft + spotW / 2 - tooltipW / 2), vw - tooltipW - 8),
        };
        break;
      case "left":
        style = {
          ...style,
          top: Math.min(Math.max(8, spotTop + spotH / 2 - tooltipH / 2), vh - tooltipH - 8),
          left: Math.max(8, spotLeft - gap - tooltipW),
        };
        break;
      case "right":
        style = {
          ...style,
          top: Math.min(Math.max(8, spotTop + spotH / 2 - tooltipH / 2), vh - tooltipH - 8),
          left: Math.min(spotRight + gap, vw - tooltipW - 8),
        };
        break;
      case "bottom":
      default:
        style = {
          ...style,
          top: Math.min(spotBottom + gap, vh - tooltipH - 8),
          left: Math.min(Math.max(8, spotLeft + spotW / 2 - tooltipW / 2), vw - tooltipW - 8),
        };
        break;
    }

    setTooltipStyle(style);
  }, [currentStep, resolveTarget]);

  // Reposition on step change, scroll, resize, and via ResizeObserver.
  useLayoutEffect(() => {
    reposition();
  }, [reposition, stepIndex]);

  useEffect(() => {
    if (!currentStep) return;
    const handleScroll = () => reposition();
    const handleResize = () => reposition();

    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);

    let ro: ResizeObserver | null = null;
    const el = resolveTarget();
    if (el && el !== document.body) {
      ro = new ResizeObserver(() => reposition());
      ro.observe(el);
    }

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
      ro?.disconnect();
    };
  }, [currentStep, reposition, resolveTarget, stepIndex]);

  // Execute step action on entry (e.g. click a tab to reveal a target).
  // The click selector resolves within `action.scope` (or the step's target
  // if no scope is given), so it stays relative rather than global.
  // Uses full pointer event sequence (pointerdown → mousedown → focus → mouseup → click)
  // so it works with frameworks like Radix UI that listen for pointerdown.
  useEffect(() => {
    const step = steps[stepIndex];
    if (step?.action?.click) {
      const scopeSelector = step.action.scope ?? step.target;
      if (scopeSelector !== "body") {
        const scope = document.querySelector(scopeSelector);
        if (scope) {
          let el: HTMLElement | null = null;
          if (scope.matches?.(step.action.click)) {
            el = scope as HTMLElement;
          } else {
            el = scope.querySelector(step.action.click);
          }
          if (el && el instanceof HTMLElement) {
            const opts: MouseEventInit = { bubbles: true, cancelable: true, view: window, button: 0 };
            el.dispatchEvent(new PointerEvent("pointerdown", { ...opts, pointerType: "mouse" }));
            el.dispatchEvent(new MouseEvent("mousedown", opts));
            el.focus();
            el.dispatchEvent(new MouseEvent("mouseup", opts));
            el.dispatchEvent(new MouseEvent("click", opts));
          }
        }
      }
    }
  }, [stepIndex, steps]);

  // Scroll the target into view (only for non-body steps).
  useEffect(() => {
    const el = resolveTarget();
    if (!el || el === document.body) return;
    const rect = el.getBoundingClientRect();
    const inViewport =
      rect.top >= 0 && rect.bottom <= window.innerHeight &&
      rect.left >= 0 && rect.right <= window.innerWidth;
    if (!inViewport) {
      el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
      // Reposition after scroll settles.
      setTimeout(reposition, 300);
    }
  }, [resolveTarget, reposition, stepIndex]);

  // Keyboard navigation.
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      switch (e.key) {
        case "Escape":
          e.preventDefault();
          onClose();
          break;
        case "ArrowRight":
          e.preventDefault();
          onNext();
          break;
        case "ArrowLeft":
          e.preventDefault();
          onBack();
          break;
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose, onNext, onBack]);

  if (!currentStep) return null;

  const spotlightRadius = currentStep.data?.spotlightRadius ?? DEFAULT_SPOTLIGHT_RADIUS;
  const isCentered = currentStep.target === "body";

  return createPortal(
    <>
      {/* Overlay with box-shadow spotlight */}
      {!isCentered && spotRect && (
        <div
          aria-hidden
          className="fixed inset-0"
          style={{
            boxShadow: `0 0 0 9999px rgba(0, 0, 0, 0.55)`,
            borderRadius: `${spotlightRadius}px`,
            top: `${spotRect.top}px`,
            left: `${spotRect.left}px`,
            width: `${spotRect.width}px`,
            height: `${spotRect.height}px`,
            zIndex: 10000,
            transition: "all 300ms cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        />
      )}
      {/* Centered overlay (no spotlight) */}
      {isCentered && (
        <div
          aria-hidden
          className="fixed inset-0 bg-black/55 backdrop-blur-[1px]"
          style={{ zIndex: 10000 }}
        />
      )}
      {/* Pulsing ring around the spotlight target */}
      {!isCentered && spotRect && (
        <div
          aria-hidden
          className="pointer-events-none fixed rounded-xl ring-2 ring-primary/40 animate-pulse"
          style={{
            top: `${spotRect.top}px`,
            left: `${spotRect.left}px`,
            width: `${spotRect.width}px`,
            height: `${spotRect.height}px`,
            borderRadius: `${spotlightRadius}px`,
            zIndex: 10000,
          }}
        />
      )}
      {/* Tooltip */}
      <div ref={tooltipRef} style={tooltipStyle}>
        {renderTooltip({
          step: currentStep,
          index: stepIndex,
          size: steps.length,
          isLastStep: stepIndex === steps.length - 1,
          onNext,
          onBack,
          onClose,
          onSnooze,
          onJump,
          tooltipRef,
          tooltipStyle,
        })}
      </div>
    </>,
    document.body
  );
};

export default TourEngine;

export type TooltipRendererParams = Parameters<TourEngineProps["renderTooltip"]>[0];